import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';

import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import RoomCard from './components/RoomCard';
import RoomTable from './components/RoomTable';
import RoomFilters from './components/RoomFilters';
import RoomDetailsPanel from './components/RoomDetailsPanel';
import RoomForm from './components/RoomForm';
import RoomStats from './components/RoomStats';
import RoomInsights from './components/RoomInsights';
import { roomService, authService } from '../../services/api';

const RoomManagement = () => {
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'cards'
  const [selectedRooms, setSelectedRooms] = useState([]);
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleCreateReservation = () => {
    navigate('/reservations');
  };
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedType, setSelectedType] = useState('');
  const [selectedStatus, setSelectedStatus] = useState('');
  const [selectedAmenities, setSelectedAmenities] = useState({});
  const [showDetailsPanel, setShowDetailsPanel] = useState(false);
  const [showRoomForm, setShowRoomForm] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [formMode, setFormMode] = useState('create'); // 'create', 'edit', 'duplicate'
  const [showBulkActions, setShowBulkActions] = useState(false);
  const [loading, setLoading] = useState(true);

    // Carregar dados reais
  const [rooms, setRooms] = useState([]);

  // Carregar quartos da API
  useEffect(() => {
    const loadRooms = async () => {
      try {
        setLoading(true);
        const selectedHotel = authService.getSelectedHotel();
        const roomsData = await roomService.getAll(
          selectedHotel?.id ? { hotelId: selectedHotel.id } : {}
        );
        setRooms(roomsData);
      } catch (error) {
        console.error('Erro ao carregar quartos:', error);
        setRooms([]);
      } finally {
        setLoading(false);
      }
    };

    loadRooms();
  }, []);

  // Recarregar quando o hotel mudar
  useEffect(() => {
    const onHotelChanged = () => {
      // reusar a mesma função
      (async () => {
        try {
          setLoading(true);
          const selectedHotel = authService.getSelectedHotel();
          const roomsData = await roomService.getAll(
            selectedHotel?.id ? { hotelId: selectedHotel.id } : {}
          );
          setRooms(roomsData);
        } catch (error) {
          console.error('Erro ao recarregar quartos após troca de hotel:', error);
          setRooms([]);
        } finally {
          setLoading(false);
        }
      })();
    };
    window.addEventListener('hotelChanged', onHotelChanged);
    return () => window.removeEventListener('hotelChanged', onHotelChanged);
  }, []);

  // Filter rooms based on search and filters
  const filteredRooms = rooms.filter(room => {
    // Obter o tipo como string (pode ser objeto {name, label} ou string)
    const roomTypeString = typeof room.type === 'object' && room.type !== null
      ? (room.type.label || room.type.name || '')
      : (room.type || '');
    
    const matchesSearch = room.number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         roomTypeString.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = !selectedType || roomTypeString === selectedType || room.type === selectedType;
    const matchesStatus = !selectedStatus || room.status === selectedStatus;
    const matchesAmenities = Object.entries(selectedAmenities).every(([key, value]) => 
      !value || room[key] === true
    );

    return matchesSearch && matchesType && matchesStatus && matchesAmenities;
  });

  // Handle room selection
  const handleSelectRoom = (roomId, isSelected) => {
    setSelectedRooms(prev => 
      isSelected 
        ? [...prev, roomId]
        : prev.filter(id => id !== roomId)
    );
  };

  const handleSelectAll = (isSelected) => {
    setSelectedRooms(isSelected ? filteredRooms.map(room => room.id) : []);
  };

  // Handle room actions
  const handleViewDetails = (room) => {
    setSelectedRoom(room);
    setShowDetailsPanel(true);
  };

  const handleEditRoom = (room) => {
    setSelectedRoom(room);
    setFormMode('edit');
    setShowRoomForm(true);
    setShowDetailsPanel(false);
  };

  const handleDuplicateRoom = (room) => {
    setSelectedRoom(room);
    setFormMode('duplicate');
    setShowRoomForm(true);
  };

  const handleDeleteRoom = async (room) => {
    if (window.confirm(`Tem certeza que deseja excluir o quarto ${room.number}?`)) {
      try {
        await roomService.delete(room.id);
        setRooms(prev => prev.filter(r => r.id !== room.id));
        setSelectedRooms(prev => prev.filter(id => id !== room.id));
      } catch (error) {
        console.error('Erro ao deletar quarto:', error);
        alert('Erro ao deletar quarto. Tente novamente.');
      }
    }
  };

  const handleAddRoom = () => {
    setSelectedRoom(null);
    setFormMode('create');
    setShowRoomForm(true);
  };

  const handleSaveRoom = async (roomData) => {
    try {
      if (formMode === 'edit') {
        const updatedRoom = await roomService.update(selectedRoom.id, roomData);
        setRooms(prev => prev.map(room => 
          room.id === selectedRoom.id ? updatedRoom : room
        ));
      } else {
        const newRoom = await roomService.create(roomData);
        setRooms(prev => [...prev, newRoom]);
      }
      
      setShowRoomForm(false);
      setSelectedRoom(null);
    } catch (error) {
      console.error('Erro ao salvar quarto:', error);
      alert('Erro ao salvar quarto. Tente novamente.');
    }
  };

  // Handle bulk actions
  const handleBulkAction = (action) => {
    const selectedRoomObjects = rooms.filter(room => selectedRooms.includes(room.id));
    
    switch (action) {
      case 'delete':
        if (window.confirm(`Tem certeza que deseja excluir ${selectedRooms.length} quartos?`)) {
          setRooms(prev => prev.filter(room => !selectedRooms.includes(room.id)));
          setSelectedRooms([]);
        }
        break;
      case 'maintenance':
        setRooms(prev => prev.map(room => 
          selectedRooms.includes(room.id) ? { ...room, status: 'Manutenção' } : room
        ));
        setSelectedRooms([]);
        break;
      case 'available':
        setRooms(prev => prev.map(room => 
          selectedRooms.includes(room.id) ? { ...room, status: 'Disponível' } : room
        ));
        setSelectedRooms([]);
        break;
      case 'cleaning':
        setRooms(prev => prev.map(room => 
          selectedRooms.includes(room.id) ? { ...room, status: 'Limpeza' } : room
        ));
        setSelectedRooms([]);
        break;
    }
    setShowBulkActions(false);
  };

  // Handle filter changes
  const handleClearFilters = () => {
    setSearchTerm('');
    setSelectedType('');
    setSelectedStatus('');
    setSelectedAmenities({});
  };

  const handleAmenitiesChange = (amenity, isSelected) => {
    setSelectedAmenities(prev => ({
      ...prev,
      [amenity]: isSelected
    }));
  };

  const [selectedHotel, setSelectedHotel] = useState(null);

  useEffect(() => {
    const storedHotel = localStorage.getItem('selectedHotel');
    if (storedHotel) {
      try {
        setSelectedHotel(JSON.parse(storedHotel));
      } catch (error) {
        console.error('Erro ao parsear hotel selecionado:', error);
      }
    }
  }, []);

  return (
    <div className="min-h-screen bg-background">
      <Header 
        onCreateReservation={handleCreateReservation} 
        onMenuClick={() => setIsMobileMenuOpen(true)}
      />
      <Sidebar 
        isOpen={isMobileMenuOpen} 
        onClose={() => setIsMobileMenuOpen(false)}
      />
      
      <main className="lg:ml-60 pt-16">
        <div className="p-4 sm:p-6">
          <div className="max-w-7xl mx-auto">
            <Breadcrumb />
            
            {/* Page Header */}
            <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6 mb-8">
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h1 className="text-3xl font-bold text-foreground">Gestão de Quartos</h1>
                  {selectedHotel && (
                    <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                      {selectedHotel.name}
                    </span>
                  )}
                </div>
                <p className="text-muted-foreground text-sm max-w-2xl">
                  Gerencie o inventário completo de quartos, preços, comodidades e disponibilidade
                </p>
              </div>
              
              <div className="flex flex-wrap items-center gap-3">
                {/* View Mode Toggle */}
                <div className="flex items-center bg-muted/30 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('table')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                      viewMode === 'table' 
                        ? 'bg-primary text-primary-foreground shadow-sm' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    title="Visualização em Tabela"
                  >
                    <Icon name="Table" size={16} />
                    <span className="hidden sm:inline">Tabela</span>
                  </button>
                  <button
                    onClick={() => setViewMode('cards')}
                    className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-all ${
                      viewMode === 'cards' 
                        ? 'bg-primary text-primary-foreground shadow-sm' 
                        : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                    }`}
                    title="Visualização em Cards"
                  >
                    <Icon name="Grid3X3" size={16} />
                    <span className="hidden sm:inline">Cards</span>
                  </button>
                </div>

                {/* Bulk Actions */}
                {selectedRooms.length > 0 && (
                  <div className="relative">
                    <button
                      onClick={() => setShowBulkActions(!showBulkActions)}
                      className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-card text-foreground font-medium rounded-lg border border-border hover:bg-accent transition-all duration-200 hover:shadow-sm"
                    >
                      <Icon name="MoreVertical" size={18} />
                      <span>Ações ({selectedRooms.length})</span>
                    </button>
                    
                    {showBulkActions && (
                      <div className="absolute top-full right-0 mt-2 w-56 bg-card border border-border rounded-xl shadow-lg z-10 overflow-hidden">
                        <div className="p-2">
                          <button
                            onClick={() => handleBulkAction('available')}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent transition-all text-left group"
                          >
                            <Icon name="CheckCircle" size={18} className="text-success" />
                            <span className="text-sm text-foreground group-hover:text-success">Marcar Disponível</span>
                          </button>
                          <button
                            onClick={() => handleBulkAction('maintenance')}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent transition-all text-left group"
                          >
                            <Icon name="Wrench" size={18} className="text-warning" />
                            <span className="text-sm text-foreground group-hover:text-warning">Marcar Manutenção</span>
                          </button>
                          <button
                            onClick={() => handleBulkAction('cleaning')}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-accent transition-all text-left group"
                          >
                            <Icon name="Sparkles" size={18} className="text-secondary" />
                            <span className="text-sm text-foreground group-hover:text-secondary">Marcar Limpeza</span>
                          </button>
                          <div className="border-t border-border my-1.5" />
                          <button
                            onClick={() => handleBulkAction('delete')}
                            className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-destructive/10 transition-all text-left group"
                          >
                            <Icon name="Trash2" size={18} className="text-destructive" />
                            <span className="text-sm text-destructive">Excluir Selecionados</span>
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                <button
                  onClick={handleAddRoom}
                  className="inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
                >
                  <Icon name="Plus" size={18} />
                  <span>Adicionar Quarto</span>
                </button>
              </div>
            </div>

            {/* Main Stats */}
            <div className="mb-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {(() => {
                  const totalRooms = rooms.length;
                  
                  const availableRooms = rooms.filter(room => room.status === 'AVAILABLE').length;
                  const occupiedRooms = rooms.filter(room => room.status === 'OCCUPIED').length;
                  const maintenanceRooms = rooms.filter(room => room.status === 'MAINTENANCE').length;
                  const cleaningRooms = rooms.filter(room => room.status === 'CLEANING').length;
                  
                  const roomsWithActiveReservations = rooms.filter(room => 
                    room.reservations && room.reservations.length > 0
                  ).length;
                  
                  const occupancyRate = totalRooms > 0 ? 
                    ((occupiedRooms + roomsWithActiveReservations) / totalRooms * 100).toFixed(1) : 0;
                  
                  const totalRevenue = rooms.reduce((sum, room) => {
                    const roomRevenue = room.reservations ? room.reservations.reduce((roomSum, reservation) => {
                      return roomSum + parseFloat(reservation.totalAmount || 0);
                    }, 0) : 0;
                    return sum + roomRevenue;
                  }, 0);

                  const averagePrice = totalRooms > 0 ? 
                    (rooms.reduce((sum, room) => sum + parseFloat(room.couplePrice || 0), 0) / totalRooms).toFixed(2) : 0;

                  const formatPrice = (price) => {
                    return new Intl.NumberFormat('pt-BR', {
                      style: 'currency',
                      currency: 'BRL'
                    }).format(price);
                  };

                  const stats = [
                    {
                      title: 'Total de Quartos',
                      value: totalRooms,
                      icon: 'Bed',
                      color: 'primary',
                      trend: '+12%'
                    },
                    {
                      title: 'Taxa de Ocupação',
                      value: `${occupancyRate}%`,
                      icon: 'TrendingUp',
                      color: 'success',
                      trend: '+8%'
                    },
                    {
                      title: 'Receita Mensal',
                      value: formatPrice(totalRevenue),
                      icon: 'DollarSign',
                      color: 'success',
                      trend: '+15%'
                    },
                    {
                      title: 'Preço Médio',
                      value: formatPrice(averagePrice),
                      icon: 'Calculator',
                      color: 'secondary',
                      trend: '+5%'
                    }
                  ];

                  return stats.map((stat, index) => (
                    <div 
                      key={index} 
                      className="group relative bg-gradient-to-br from-card to-card/50 border border-border rounded-xl p-5 hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden"
                    >
                      <div className={`absolute top-0 right-0 w-32 h-32 bg-${stat.color}/5 rounded-full blur-3xl opacity-20 -mr-16 -mt-16 group-hover:opacity-30 transition-opacity`}></div>
                      
                      <div className="relative">
                        <div className="flex items-start justify-between mb-4">
                          <div className={`p-2.5 rounded-xl bg-${stat.color}/10 group-hover:scale-110 transition-transform duration-300`}>
                            <Icon name={stat.icon} size={22} className={`text-${stat.color}`} />
                          </div>
                          <div className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-success/10">
                            <Icon name="TrendingUp" size={12} className="text-success" />
                            <span className="text-xs font-semibold text-success">{stat.trend}</span>
                          </div>
                        </div>
                        
                        <div>
                          <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                            {stat.title}
                          </h3>
                          <p className="text-2xl font-bold text-foreground">
                            {stat.value}
                          </p>
                        </div>
                        
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>

          {/* Filters */}
          <div className="mb-6">
            <RoomFilters
              searchTerm={searchTerm}
              onSearchChange={setSearchTerm}
              selectedType={selectedType}
              onTypeChange={setSelectedType}
              selectedStatus={selectedStatus}
              onStatusChange={setSelectedStatus}
              selectedAmenities={selectedAmenities}
              onAmenitiesChange={handleAmenitiesChange}
              onClearFilters={handleClearFilters}
            />
          </div>

            {/* Results Summary */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-6 p-4 bg-gradient-to-r from-muted/20 to-muted/10 rounded-lg border border-border/50">
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-2">
                  <Icon name="Search" size={18} className="text-primary" />
                  <p className="text-sm font-medium text-foreground">
                    Mostrando <span className="text-primary font-bold">{filteredRooms.length}</span> de <span className="font-bold">{rooms.length}</span> quartos
                  </p>
                </div>
              </div>
              {selectedRooms.length > 0 && (
                <div className="flex items-center gap-2 px-3 py-1.5 bg-primary/10 rounded-lg border border-primary/20">
                  <Icon name="CheckCircle" size={16} className="text-primary" />
                  <p className="text-sm font-semibold text-primary">
                    {selectedRooms.length} quarto{selectedRooms.length > 1 ? 's' : ''} selecionado{selectedRooms.length > 1 ? 's' : ''}
                  </p>
                </div>
              )}
            </div>

            {/* Room List */}
            {filteredRooms.length > 0 ? (
              <>
                {viewMode === 'table' ? (
                  <RoomTable
                    rooms={filteredRooms}
                    onEdit={handleEditRoom}
                    onDuplicate={handleDuplicateRoom}
                    onDelete={handleDeleteRoom}
                    onViewDetails={handleViewDetails}
                    selectedRooms={selectedRooms}
                    onSelectRoom={handleSelectRoom}
                    onSelectAll={handleSelectAll}
                  />
                ) : (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
                    {filteredRooms.map((room) => (
                      <RoomCard
                        key={room.id}
                        room={room}
                        onEdit={handleEditRoom}
                        onDuplicate={handleDuplicateRoom}
                        onDelete={handleDeleteRoom}
                        onViewDetails={handleViewDetails}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="relative bg-gradient-to-br from-card to-card/50 border border-border rounded-xl p-12 text-center overflow-hidden">
                <div className="absolute top-0 right-0 w-64 h-64 bg-primary/5 rounded-full blur-3xl opacity-20 -mr-32 -mt-32"></div>
                <div className="relative">
                  <div className="w-20 h-20 mx-auto mb-6 bg-primary/10 rounded-full flex items-center justify-center">
                    <Icon name="Bed" size={40} className="text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-foreground mb-2">Nenhum quarto encontrado</h3>
                  <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                    Tente ajustar os filtros de busca ou adicione um novo quarto ao seu inventário
                  </p>
                  <div className="flex flex-wrap justify-center gap-3">
                    <button
                      onClick={handleClearFilters}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-card text-foreground font-medium rounded-lg border border-border hover:bg-accent transition-all"
                    >
                      <Icon name="RotateCcw" size={18} />
                      <span>Limpar Filtros</span>
                    </button>
                    <button
                      onClick={handleAddRoom}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground font-medium rounded-lg hover:bg-primary/90 transition-all shadow-sm"
                    >
                      <Icon name="Plus" size={18} />
                      <span>Adicionar Quarto</span>
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Status dos Quartos */}
            {filteredRooms.length > 0 && (
              <div className="mt-8">
                <div className="group relative bg-gradient-to-br from-card to-card/50 border border-border rounded-xl p-6 hover:shadow-lg transition-all duration-300 overflow-hidden">
                  <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full blur-3xl opacity-20 -mr-16 -mt-16 group-hover:opacity-30 transition-opacity"></div>
                  
                  <div className="relative">
                    <div className="flex items-center gap-3 mb-6">
                      <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                        <Icon name="LayoutGrid" size={20} className="text-primary" />
                      </div>
                      <div>
                        <h3 className="text-xl font-bold text-foreground">Status dos Quartos</h3>
                        <p className="text-sm text-muted-foreground">Distribuição por estado de disponibilidade</p>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                      {(() => {
                        const availableRooms = rooms.filter(room => room.status === 'AVAILABLE').length;
                        const occupiedRooms = rooms.filter(room => room.status === 'OCCUPIED').length;
                        const maintenanceRooms = rooms.filter(room => room.status === 'MAINTENANCE').length;
                        const cleaningRooms = rooms.filter(room => room.status === 'CLEANING').length;

                        const statusStats = [
                          {
                            label: 'Disponíveis',
                            value: availableRooms,
                            icon: 'CheckCircle',
                            color: 'success'
                          },
                          {
                            label: 'Ocupados',
                            value: occupiedRooms,
                            icon: 'BedDouble',
                            color: 'destructive'
                          },
                          {
                            label: 'Manutenção',
                            value: maintenanceRooms,
                            icon: 'Wrench',
                            color: 'warning'
                          },
                          {
                            label: 'Limpeza',
                            value: cleaningRooms,
                            icon: 'Sparkles',
                            color: 'secondary'
                          }
                        ];

                        return statusStats.map((status, index) => (
                          <div key={index} className="relative bg-gradient-to-br from-muted/20 to-muted/10 rounded-lg p-4 border border-border/50 hover:border-primary/30 transition-all group/status">
                            <div className="text-center">
                              <div className={`w-14 h-14 mx-auto rounded-full flex items-center justify-center bg-${status.color}/10 mb-3 group-hover/status:scale-110 transition-transform duration-300`}>
                                <Icon name={status.icon} size={24} className={`text-${status.color}`} />
                              </div>
                              <p className={`text-3xl font-bold text-${status.color} mb-1`}>{status.value}</p>
                              <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{status.label}</p>
                            </div>
                          </div>
                        ));
                      })()}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Insights */}
            {filteredRooms.length > 0 && (
              <div className="mt-8">
                <RoomInsights rooms={rooms} />
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Room Details Panel */}
      <RoomDetailsPanel
        room={selectedRoom}
        isOpen={showDetailsPanel}
        onClose={() => setShowDetailsPanel(false)}
        onEdit={handleEditRoom}
      />

      {/* Room Form Modal */}
      <RoomForm
        room={selectedRoom}
        isOpen={showRoomForm}
        onClose={() => setShowRoomForm(false)}
        onSave={handleSaveRoom}
        mode={formMode}
      />
    </div>
  );
};

export default RoomManagement;